import React from 'react';
import { MapPin, Home, Bed, Bath, User, Mail, Camera } from 'lucide-react';
import { SOW } from '../../models/sow';
import { Badge } from '../ui/badge';

interface ProjectHeroProps {
  sow: SOW;
  images?: any[];
  onViewAllImages?: () => void;
}

export const ProjectHero: React.FC<ProjectHeroProps> = ({ sow, images = [], onViewAllImages }) => {
  // Get images for gallery preview
  const allImages = images;
  const totalImages = allImages.length;
  const previewImages = allImages.slice(0, 4);
  const primaryImage = allImages[0];

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Mobile: Stack Vertical */}
      <div className="lg:hidden">
        {/* Compact Image Preview */}
        {primaryImage && (
          <div className="p-4 border-b border-gray-100">
            <button
              onClick={onViewAllImages}
              className="relative w-full aspect-video bg-gray-100 rounded-lg overflow-hidden group"
            >
              <img
                src={primaryImage.url}
                alt={primaryImage.description || 'Project photo'}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />

              {/* Image Count Badge - Top Right */}
              {totalImages > 1 && (
                <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5">
                  <Camera className="h-3 w-3" />
                  {totalImages}
                </div>
              )}
            </button>
          </div>
        )}

        {/* Project Info */}
        <div className="p-4 space-y-3">
          <div>
            <div className="flex items-start gap-2 mb-1">
              <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <h2 className="text-lg font-semibold text-gray-900">{sow.address}</h2>
            </div>
            {sow.scopeDefinition && (
              <div className="ml-6 flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-600">{sow.scopeDefinition.projectType}</span>
                <span className="text-gray-300">•</span>
                <Badge variant="outline" className="text-xs">
                  {sow.scopeDefinition.finishLevel}
                </Badge>
              </div>
            )}
          </div>

          {sow.propertyData && (
            <div className="flex items-center gap-3 text-sm text-gray-600 flex-wrap">
              {sow.propertyData.squareFootage && (
                <div className="flex items-center gap-1">
                  <Home className="h-4 w-4" />
                  <span>{sow.propertyData.squareFootage.toLocaleString()} sf</span>
                </div>
              )}
              {sow.propertyData.bedrooms && (
                <>
                  <span className="text-gray-300">•</span>
                  <div className="flex items-center gap-1">
                    <Bed className="h-4 w-4" />
                    <span>{sow.propertyData.bedrooms} bd</span>
                  </div>
                </>
              )}
              {sow.propertyData.bathrooms && (
                <>
                  <span className="text-gray-300">•</span>
                  <div className="flex items-center gap-1">
                    <Bath className="h-4 w-4" />
                    <span>{sow.propertyData.bathrooms} ba</span>
                  </div>
                </>
              )}
            </div>
          )}

          {sow.customerInfo && (
            <div className="pt-3 border-t border-gray-100 space-y-1 text-sm">
              <div className="flex items-center gap-2 text-gray-700">
                <User className="h-4 w-4 text-gray-400" />
                <span>{sow.customerInfo.name}</span>
              </div>
              {sow.customerInfo.email && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="truncate">{sow.customerInfo.email}</span>
                </div>
              )}
            </div>
          )}

          {sow.jobs && sow.jobs.length > 0 && (
            <div className="pt-3 border-t border-gray-100">
              <div className="flex flex-wrap gap-1.5">
                {sow.jobs.map((job: any, idx: number) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {job.label}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Desktop: Side by Side with Smaller Image */}
      <div className="hidden lg:grid lg:grid-cols-[300px,1fr] lg:gap-6 p-6">
        {/* Left: Compact Image Gallery - Fixed Width 300px */}
        <div className="space-y-3">
          {primaryImage ? (
            <>
              <div
                className="relative w-full aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer group"
                onClick={onViewAllImages}
              >
                <img
                  src={primaryImage.url}
                  alt={primaryImage.description || 'Project photo'}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />

                {/* Gradient Overlay on Hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Image Count Badge - Top Right */}
                {totalImages > 1 && (
                  <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5">
                    <Camera className="h-3 w-3" />
                    {totalImages}
                  </div>
                )}

                {/* View All Overlay - Shows on Hover */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-white/95 backdrop-blur-sm text-gray-900 px-4 py-2 rounded-lg font-medium text-sm shadow-lg flex items-center gap-2">
                    <Camera className="h-4 w-4" />
                    View All Photos
                  </div>
                </div>
              </div>

              {/* Thumbnail Grid - Only if 2+ images */}
              {totalImages > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {previewImages.slice(1, 4).map((img: any, idx: number) => (
                    <button
                      key={img.id || idx}
                      onClick={onViewAllImages}
                      className="relative aspect-square rounded-md border border-gray-200 overflow-hidden hover:border-blue-400 hover:shadow-sm transition-all bg-gray-50"
                    >
                      <img
                        src={img.url}
                        alt={img.description || `Photo ${idx + 2}`}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      />
                    </button>
                  ))}

                  {/* "+X more" button if more than 4 images */}
                  {totalImages > 4 && (
                    <button
                      onClick={onViewAllImages}
                      className="aspect-square rounded-md border border-gray-200 bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-colors flex flex-col items-center justify-center text-xs font-semibold text-blue-700"
                    >
                      <span className="text-lg">+{totalImages - 4}</span>
                      <span className="text-[10px]">more</span>
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="w-full aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
              <div className="text-center">
                <Camera className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-500">No photos yet</p>
                <p className="text-xs text-gray-400 mt-1">Upload during questionnaire</p>
              </div>
            </div>
          )}
        </div>

        {/* Right: Project Details */}
        <div className="space-y-4">
          {/* Address & Project Type */}
          <div>
            <div className="flex items-start gap-2 mb-2">
              <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <h2 className="text-2xl font-bold text-gray-900">{sow.address}</h2>
            </div>
            {sow.scopeDefinition && (
              <div className="ml-7 flex items-center gap-2 flex-wrap">
                <span className="text-base text-gray-600">{sow.scopeDefinition.projectType}</span>
                <span className="text-gray-300">•</span>
                <Badge variant="outline">
                  {sow.scopeDefinition.finishLevel}
                </Badge>
              </div>
            )}
          </div>

          {/* Property Stats */}
          {sow.propertyData && (
            <div className="flex items-center gap-4 text-base text-gray-600 flex-wrap">
              {sow.propertyData.squareFootage && (
                <div className="flex items-center gap-2">
                  <Home className="h-5 w-5" />
                  <span>{sow.propertyData.squareFootage.toLocaleString()} sf</span>
                </div>
              )}
              {sow.propertyData.bedrooms && (
                <>
                  <span className="text-gray-300">•</span>
                  <div className="flex items-center gap-2">
                    <Bed className="h-5 w-5" />
                    <span>{sow.propertyData.bedrooms} beds</span>
                  </div>
                </>
              )}
              {sow.propertyData.bathrooms && (
                <>
                  <span className="text-gray-300">•</span>
                  <div className="flex items-center gap-2">
                    <Bath className="h-5 w-5" />
                    <span>{sow.propertyData.bathrooms} baths</span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Customer Info */}
          {sow.customerInfo && (
            <div className="pt-4 border-t border-gray-100 space-y-2">
              <div className="flex items-center gap-2 text-gray-700">
                <User className="h-5 w-5 text-gray-400" />
                <span className="font-medium">{sow.customerInfo.name}</span>
              </div>
              {sow.customerInfo.email && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <span>{sow.customerInfo.email}</span>
                </div>
              )}
            </div>
          )}

          {/* Scope Components */}
          {sow.jobs && sow.jobs.length > 0 && (
            <div className="pt-4 border-t border-gray-100">
              <div className="text-sm font-medium text-gray-700 mb-2">Project Scope:</div>
              <div className="flex flex-wrap gap-2">
                {sow.jobs.map((job: any, idx: number) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {job.label}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
